import { memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useAnalytics } from 'use-analytics';
import dayjs from 'dayjs';
import DateFilter from '../../../../Filters/DateSelectorFilter';
import { updateFlightSearchObj } from '../../../../../store/slices/flight';
import { setFlightTripSearchInputs } from '../../../../../store/slices/tripSearch';
import flightSearchInput from '../../../../../helpers/selectors/flightSearchInput';
import { FlightSearchTypes, TravelTypes } from '../../../../../constants';
import { getTravelPolicyLeadDays } from '../../../../../store/slices/user';

const LeaveDateFlightFilter = ({
  tripId, variant, filterButtonMaxWidth, label, flightRowIndex,
  doOpenDatePicker, setDoOpenDatePicker,
}) => {
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const isTripSearch = !tripId;
  const { searchInputs, defaultSearchObj } = useSelector(flightSearchInput(tripId, isTripSearch));
  const { travelAgency } = useSelector((store) => store.user);
  const leadDays = useSelector(getTravelPolicyLeadDays(TravelTypes.flight));
  const flightLeaveDateLabel = travelAgency && travelAgency.filterSettings
  && travelAgency.filterSettings.flightLeaveDateLabel;
  const handleChange = (newValue) => {
    if (newValue) {
      setDoOpenDatePicker(false, flightRowIndex);
    }
    let returnDate = null;
    if (searchInputs.flightSearchType === FlightSearchTypes.ROUND_TRIP
      || (searchInputs.flightSearchType === FlightSearchTypes.MULTI_CITY && flightRowIndex === 0)
    ) {
      returnDate = searchInputs
      && searchInputs.flightSegments[1] && searchInputs.flightSegments[1].leaveDate;
    }

    if (!returnDate || dayjs(newValue).diff(returnDate, 'day') >= 0) {
      returnDate = dayjs(newValue).add(1, 'day').format('YYYY-MM-DD');
    }
    const payload = {
      tripId,
      searchObj: {
        flightSegments: searchInputs.flightSegments ? [...searchInputs.flightSegments] : [],
        page: 1,
      },
    };
    payload.searchObj.flightSegments[flightRowIndex] = {
      ...payload.searchObj.flightSegments[flightRowIndex],
      leaveDate: newValue,
    };
    if (returnDate && (searchInputs.flightSearchType === FlightSearchTypes.ROUND_TRIP
      || (searchInputs.flightSearchType === FlightSearchTypes.MULTI_CITY && flightRowIndex === 0)
    )) {
      payload.searchObj.flightSegments[1] = {
        ...payload.searchObj.flightSegments[1],
        leaveDate: returnDate,
      };
    }
    if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
    else dispatch(updateFlightSearchObj(payload));
    track('leaveDateFlightFilter.handleChange', {
      leaveDate: newValue,
    });
  };

  const value = useMemo(() => searchInputs
      && searchInputs.flightSegments
      && searchInputs.flightSegments[flightRowIndex]
      && searchInputs.flightSegments[flightRowIndex].leaveDate, [flightRowIndex, searchInputs]);

  const hasMinDate = flightRowIndex > 0
    && searchInputs.flightSearchType === FlightSearchTypes.MULTI_CITY;

  const minDateValue = useMemo(() => hasMinDate && searchInputs
    && searchInputs.flightSegments[flightRowIndex - 1]
    && searchInputs.flightSegments[flightRowIndex - 1].leaveDate,
  [flightRowIndex, hasMinDate, searchInputs]);

  return (
    <DateFilter
      onChange={handleChange}
      label={value ? '' : (flightLeaveDateLabel || label)}
      value={value}
      defaultValue={isTripSearch ? value : defaultSearchObj.leaveDate}
      variant={variant}
      filterButtonMaxWidth={filterButtonMaxWidth}
      minDate={!minDateValue ? undefined : dayjs(minDateValue).format('MM/DD/YYYY')}
      doOpenDatePicker={doOpenDatePicker}
      setDoOpenDatePicker={(open) => setDoOpenDatePicker(open, flightRowIndex)}
      bgClassName={isTripSearch ? 'bg-searchInput-0' : ''}
      textColorClassName={isTripSearch ? 'text-datepicker-0' : ''}
      leadDays={leadDays}
    />
  );
};

LeaveDateFlightFilter.propTypes = {
  tripId: PropTypes.string,
  variant: PropTypes.string,
  filterButtonMaxWidth: PropTypes.string,
  label: PropTypes.string,
  flightRowIndex: PropTypes.number,
  doOpenDatePicker: PropTypes.bool,
  setDoOpenDatePicker: PropTypes.func,
};

LeaveDateFlightFilter.defaultProps = {
  tripId: undefined,
  variant: 'outline',
  filterButtonMaxWidth: 'max-w-full',
  label: 'Departure Date',
  flightRowIndex: undefined,
  doOpenDatePicker: false,
  setDoOpenDatePicker: () => { },
};

export default memo(LeaveDateFlightFilter);
